import * as AnimatedCarousel from '@components/animated-carousel'
import Image from 'next/image'
import { BlockCall } from 'types/block-call'
import { useEffect, useState } from 'react'

type Props = {
  ourProducts: BlockCall[]
}

export const OurProductsCarousel = ({ ourProducts }: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [size, setSize] = useState(4)

  // Proteção contra data undefined ou vazio
  if (!ourProducts || ourProducts.length === 0) {
    return null
  }

  const nextSlide = () => {
    setCurrentIndex(prev => (prev + 1) % ourProducts.length)
  }

  // determine size responsively (SSR-safe)
  useEffect(() => {
    const update = () => {
      if (typeof window === 'undefined') return
      const w = window.innerWidth
      if (w >= 1024) setSize(4)
      else if (w >= 400) setSize(2)
      else setSize(1)
    }

    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  const prevSlide = () => {
    setCurrentIndex(
      prev => (prev - 1 + ourProducts.length) % ourProducts.length
    )
  }

  return (
    <AnimatedCarousel.Root>
      <AnimatedCarousel.Header
        transition={{
          duration: 0.7,
          delay: 0.5
        }}
      >
        <h2 className="inline-block text-black text-[1.75rem] tracking-[-1px] font-bold text-center md:text-3xl md:tracking-[-0.5px] 1.5xl:text-[2.1875rem] 1.5xl:tracking-[-0.5px] py-2">
          Operação e Manutenção
        </h2>
        <p className="text-xs text-v2-eerie-black text-center md:text-base py-2">
          Conheça nossas ferramentas de operação <br />e manutenção de usinas
          solares
        </p>
      </AnimatedCarousel.Header>

      <AnimatedCarousel.Section
        transition={{
          duration: 0.7,
          delay: 1.4
        }}
      >
        {/* Placeholder para manter consistência */}
      </AnimatedCarousel.Section>

      {/* Versão mobile - carrossel com navegação */}
      <div
        className={`relative w-full h-96 md:h-[600px] rounded-xl overflow-hidden shadow-lg my-6 md:hidden`}
      >
        <Image
          alt={ourProducts[currentIndex]?.imageAlt || 'Operação e Manutenção'}
          src={
            ourProducts[currentIndex]?.imageUrl ||
            'https://www.portalsolar.com.br/assets/logo-header.svg'
          }
          fill
          className="object-cover transition-transform duration-300 rounded-lg"
          loading="lazy"
        />
        {/* Overlay com informações */}
        <div className={`absolute inset-0`}>
          <div
            className={`flex flex-col absolute rounded-lg bottom-0 h-full w-full p-4
                 ${
                   size > 2
                     ? 'items-start justify-center left-0 pl-12 bg-gradient-to-r from-black/60 rounded-lg to-transparent'
                     : 'items-center justify-end bg-gradient-to-t from-black/60 to-transparent pb-5'
                 } text-white`}
          >
            <h3 className="font-bold text-5xl mb-2 text-right">
              {ourProducts[currentIndex]?.title || ''}
            </h3>
            <span className="w-16 bg-[#7be500] h-1"></span>
            <p className="py-2 text-xl font-medium opacity-90">
              {ourProducts[currentIndex]?.description || ''}
            </p>
            {ourProducts[currentIndex]?.buttonLink !== '' && (
              <a href={ourProducts[currentIndex]?.buttonLink || '#'}>
                <button className="mt-2 px-4 py-2 bg-[#63F14B] text-black rounded-full flex justify-center items-center">
                  <span className="pt-[2px] pe-2">►</span>{' '}
                  {ourProducts[currentIndex]?.buttonText || 'Saiba Mais'}
                </button>
              </a>
            )}
          </div>
        </div>

        {/* Setas de navegação */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors duration-200"
          aria-label="Slide anterior"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors duration-200"
          aria-label="Próximo slide"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>

      {/* Indicadores de posição */}
      <div className="bottom-0 -mt-2 left-1/2 flex md:hidden space-x-2 justify-center w-full">
        {ourProducts.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-3 h-3 rounded-full transition-colors duration-200 ${
              index === currentIndex
                ? 'w-7 bg-gray-400'
                : 'hover:bg-gray-400 bg-gray-200 hover:w-7'
            }`}
            aria-label={`Ir para slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Versão desktop - mantém o primeiro item */}
      <div
        className={`relative w-full ${
          size === 1 ? 'h-[400px]' : size === 2 ? 'h-[600px]' : 'h-[700px]'
        } overflow-hidden shadow-lg my-6 hidden md:block`}
      >
        <Image
          alt={ourProducts[0]?.imageAlt}
          src={ourProducts[0]?.imageUrl}
          fill
          className="object-cover transition-transform duration-300 rounded-xl"
          loading="lazy"
        />
        {/* Overlay com informações */}
        <div
          className={`absolute inset-0 ${
            size === 1
              ? 'items-center'
              : size === 2
              ? 'items-center'
              : 'items-end'
          } flex items-end`}
        >
          <div
            className={`flex flex-col w-2/5 ${
              size === 1
                ? 'items-center'
                : 'items-start left-0 bg-gradient-to-r from-black/60 to-transparent'
            } justify-center absolute bottom-0 h-full rounded-xl p-4 text-white pl-16`}
          >
            <h3 className="font-bold text-5xl mb-2 text-right">
              {ourProducts[0]?.title || ''}
            </h3>
            <span className="w-16 bg-[#7be500] h-1"></span>
            <p className="py-2 text-xl font-medium opacity-90">
              {ourProducts[0]?.description || ''}
            </p>
            {ourProducts[0]?.buttonLink !== '' && (
              <a href={ourProducts[0]?.buttonLink || '#'}>
                <button className="mt-2 px-4 py-2 bg-[#63F14B] text-black rounded-full flex justify-center items-center">
                  <span className="pt-[2px] pe-2">►</span>{' '}
                  {ourProducts[0]?.buttonText || 'Saiba Mais'}
                </button>
              </a>
            )}
          </div>
        </div>
      </div>

      <AnimatedCarousel.Section
        transition={{
          duration: 1,
          delay: 1
        }}
        className="w-full relative"
      >
        <AnimatedCarousel.BlockCallsCarousel data={ourProducts.slice(1)} />
      </AnimatedCarousel.Section>
    </AnimatedCarousel.Root>
  )
}
